import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { consumeMenuNavigation, pageKeyFromRoute, trackUsageEvent, trackUsageSessionEnd, trackUsageSessionStart } from '../lib/usageTracking';

type Visit = {
  route: string;
  pageViewID: string;
  pageKey: string;
  brandID: string;
  siteID: string;
  visibleStartedAt?: number;
  visibleDurationMS: number;
  engagementTracked: boolean;
};

function createID() {
  try { return crypto.randomUUID(); } catch { return `page-${Date.now()}-${Math.random().toString(16).slice(2)}`; }
}

function finishVisit(visit: Visit, reason: 'route_leave' | 'pagehide', keepalive = false) {
  if (visit.engagementTracked) return;
  if (visit.visibleStartedAt !== undefined) {
    visit.visibleDurationMS += Math.max(0, performance.now() - visit.visibleStartedAt);
    visit.visibleStartedAt = undefined;
  }
  visit.engagementTracked = true;
  const durationMS = Math.round(visit.visibleDurationMS);
  if (durationMS <= 0) return;
  trackUsageEvent({
    event_name: 'page_engagement',
    page_view_id: visit.pageViewID,
    page_key: visit.pageKey,
    feature_key: 'page_tracking',
    action: 'visible_duration',
    route: visit.route,
    brand_id: visit.brandID,
    site_id: visit.siteID,
    duration_ms: durationMS,
    properties: { contract: 'page_engagement_v1', duration_mode: 'visible_total', reason },
  }, keepalive);
}

/** 仅按路由变化记录页面行为，绝不观察业务 API 请求。 */
export function UsageRouteTracker() {
  const location = useLocation();
  const brandID = useAppStore((state) => state.brandId);
  const siteID = useAppStore((state) => state.siteId);
  const visitRef = useRef<Visit | undefined>(undefined);
  const route = location.pathname;

  useEffect(() => {
    if (visitRef.current?.route === route) return undefined;
    const previousVisit = visitRef.current;
    if (previousVisit) finishVisit(previousVisit, 'route_leave');
    const visit: Visit = {
      route,
      pageViewID: createID(),
      pageKey: pageKeyFromRoute(route),
      brandID,
      siteID,
      visibleStartedAt: document.visibilityState === 'hidden' ? undefined : performance.now(),
      visibleDurationMS: 0,
      engagementTracked: false,
    };
    const previousRoute = previousVisit?.route;
    const menuNavigation = consumeMenuNavigation(route);
    visitRef.current = visit;
    trackUsageEvent({
      event_name: 'page_view',
      page_view_id: visit.pageViewID,
      page_key: visit.pageKey,
      feature_key: menuNavigation ? 'navigation_menu' : 'page_tracking',
      action: 'view',
      route,
      referrer_route: menuNavigation?.sourceRoute || previousRoute,
      brand_id: brandID,
      site_id: siteID,
      properties: menuNavigation
        ? { contract: 'page_view_v1', entry_source: 'menu', menu_key: pageKeyFromRoute(route), menu_label: menuNavigation.label }
        : { contract: 'page_view_v1', entry_source: 'route' },
    });
    return undefined;
  }, [brandID, route, siteID]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const visit = visitRef.current;
      if (!visit || visit.engagementTracked) return;
      if (document.visibilityState === 'hidden') {
        if (visit.visibleStartedAt !== undefined) {
          visit.visibleDurationMS += Math.max(0, performance.now() - visit.visibleStartedAt);
          visit.visibleStartedAt = undefined;
        }
        return;
      }
      if (visit.visibleStartedAt === undefined) visit.visibleStartedAt = performance.now();
    };
    const handlePageHide = () => {
      const visit = visitRef.current;
      if (visit) finishVisit(visit, 'pagehide', true);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  useEffect(() => {
    trackUsageSessionStart({
      page_key: pageKeyFromRoute(route),
      brand_id: brandID,
      site_id: siteID,
      properties: { contract: 'session_v1' },
    });
    const onPageHide = () => trackUsageSessionEnd({
      page_key: pageKeyFromRoute(window.location.pathname),
      brand_id: brandID,
      site_id: siteID,
      properties: { contract: 'session_v1', reason: 'pagehide' },
    });
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [brandID, route, siteID]);

  return null;
}
